import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import { RefreshCw, Download } from 'lucide-react';
import { useAutoRefresh } from '../hooks/useAutoRefresh';
import Tooltip from '../components/Tooltip';
import ajuda from '../utils/ajuda';
import { useT } from '../i18n';

const statusBadge = {
  enviado: 'badge badge-success',
  erro: 'badge badge-danger',
  pendente: 'badge badge-warning',
};

export default function Envios() {
  const { t, idioma } = useT();
  const [campanhas, setCampanhas] = useState([]);
  const [campanhaId, setCampanhaId] = useState('');
  const [envios, setEnvios] = useState([]);
  const [ultimaAtualizacao, setUltimaAtualizacao] = useState(null);
  const [carregando, setCarregando] = useState(false);

  useEffect(() => {
    api.get('/campanha/listar').then(r => setCampanhas(r.data));
  }, []);

  const buscarEnvios = useCallback(async (id) => {
    const idAtual = id !== undefined ? id : campanhaId;
    if (!idAtual) return;
    setCarregando(true);
    try {
      const r = await api.get(`/envio/campanha/${idAtual}`);
      setEnvios(r.data);
      setUltimaAtualizacao(new Date());
    } catch {} finally {
      setCarregando(false);
    }
  }, [campanhaId]);

  function handleSelecionarCampanha(id) {
    setCampanhaId(id);
    if (!id) return setEnvios([]);
    buscarEnvios(id);
  }

  useAutoRefresh(() => {
    if (campanhaId) buscarEnvios(campanhaId);
  }, 10000);

  return (
    <div>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>

      <div className="page-h">
        <div>
          <h1 className="page-h-title">{t('envios.titulo')}</h1>
          {campanhaId && ultimaAtualizacao && (
            <p className="page-h-sub">
              {t('envios.atualizado_as', { hora: ultimaAtualizacao.toLocaleTimeString(idioma === 'es' ? 'es' : 'pt-BR') })}
            </p>
          )}
        </div>
        {campanhaId && (
          <div className="page-h-actions">
            <a
              href={`${api.defaults.baseURL}/envio/campanha/${campanhaId}/csv`}
              download
              className="btn btn-secondary btn-sm"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, textDecoration: 'none' }}
              onClick={e => {
                // injeta token de auth no header não é possível via <a>, usa fetch + blob
                e.preventDefault();
                api.get(`/envio/campanha/${campanhaId}/csv`, { responseType: 'blob' }).then(r => {
                  const url = URL.createObjectURL(r.data);
                  const a = document.createElement('a');
                  a.href = url; a.download = `envios-${campanhaId}.csv`; a.click();
                  URL.revokeObjectURL(url);
                }).catch(() => {});
              }}
            >
              <Download size={13} /> {t('envios.btn_exportar_csv')}
            </a>
            <button onClick={() => buscarEnvios(campanhaId)} disabled={carregando} className="btn btn-secondary btn-sm">
              <RefreshCw size={13} style={{ animation: carregando ? 'spin 1s linear infinite' : 'none' }} />
              {t('envios.btn_atualizar')}
            </button>
          </div>
        )}
      </div>

      <div className="field" style={{ maxWidth: '420px' }}>
        <label className="label" style={{ display: 'flex', alignItems: 'center' }}>
          {t('envios.selecionar_campanha')}
          <Tooltip texto={ajuda.envios_campanha.texto} exemplo={ajuda.envios_campanha.exemplo} />
        </label>
        <select value={campanhaId} onChange={e => handleSelecionarCampanha(e.target.value)} className="select">
          <option value="">{t('envios.selecione_opcao')}</option>
          {campanhas.map(c => <option key={c.id} value={c.id}>{c.nome} — {c.status}</option>)}
        </select>
      </div>

      {envios.length > 0 && (
        <div className="card card-flush">
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-divider)', fontSize: '13px', color: 'var(--color-text-muted)' }}>
            {t('envios.contagem', { n: envios.length })}
          </div>
          <table className="table">
            <thead>
              <tr>
                {[t('envios.col_contato'), t('envios.col_telefone'), t('envios.col_status'), t('envios.col_mensagem'), t('envios.col_data')].map(h => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {envios.map(e => (
                <tr key={e.id}>
                  <td style={{ fontWeight: 500 }}>{e.contato_nome || '—'}</td>
                  <td className="table-cell-muted">{e.telefone}</td>
                  <td>
                    <span className={statusBadge[e.status] || 'badge badge-neutral'}>{e.status}</span>
                  </td>
                  <td className="table-cell-muted" style={{ maxWidth: '260px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {e.mensagem_enviada}
                  </td>
                  <td className="table-cell-muted">
                    {e.data_envio ? new Date(e.data_envio).toLocaleString(idioma === 'es' ? 'es' : 'pt-BR') : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {campanhaId && envios.length === 0 && !carregando && (
        <div className="empty">
          {t('envios.vazio')}
        </div>
      )}
    </div>
  );
}
